import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { getLogoBase64, toolData } from '../data/toolData';

// ============================================
// TEXTURE CACHE - Preload all logos at once
// ============================================
const textureCache = new Map();
const textureLoader = new THREE.TextureLoader();
let texturesPreloaded = false;

// Preload all textures immediately
function preloadAllTextures() {
  if (texturesPreloaded) return;
  texturesPreloaded = true;
  
  toolData.forEach(tool => {
    const logoBase64 = getLogoBase64(tool.name);
    if (logoBase64 && !textureCache.has(tool.name)) {
      textureLoader.load(
        logoBase64,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          textureCache.set(tool.name, tex);
        },
        undefined,
        () => textureCache.set(tool.name, null) // Mark as failed
      );
    }
  });
}

// Start preloading immediately when module loads
preloadAllTextures();

// ============================================
// CONSTANTS
// ============================================
const GLOBE_RADIUS = 1.14;
const LOGO_OFFSET = 0.08;
const POINT_SIZE = 0.025;
const SELECTED_SCALE = 1.8;
const HOVER_SCALE = 1.4;
const DRAG_SENSITIVITY = 0.003;
const MOMENTUM_DECAY = 0.95;
const IDLE_ROTATION_SPEED = 0.0001; // Very slow rotation

// ============================================
// STREAM PARTICLE SHADERS - Particles flow along curved paths
// ============================================

const streamVertexShader = `
  attribute float aSize;
  attribute float aBrightness;
  attribute float aColorIndex;
  
  varying float vBrightness;
  varying float vColorIndex;
  varying float vCamDist;
  
  void main() {
    vec3 pos = position;
    
    vBrightness = aBrightness;
    vColorIndex = aColorIndex;
    vCamDist = length(cameraPosition);
    
    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    gl_PointSize = 2.0;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const streamFragmentShader = `
  varying float vBrightness;
  varying float vColorIndex;
  varying float vCamDist;
  
  void main() {
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center) * 2.0;
    if (dist > 0.6) discard;
    
    // Blue and gold
    vec3 blue = vec3(0.15, 0.4, 0.95);
    vec3 gold = vec3(0.95, 0.7, 0.15);
    
    // Mix between blue and gold
    vec3 color = mix(blue, gold, vColorIndex);
    
    // Adjust brightness based on camera distance
    // Closer (small vCamDist ~2.5) = brighter, Further (large vCamDist ~5+) = dimmer
    float distFactor = smoothstep(2.0, 6.0, vCamDist);
    float brightness = mix(0.8, 0.25, distFactor); // 0.8 when close, 0.25 when far
    
    color *= (brightness + vBrightness * 0.2);
    
    float alpha = mix(0.9, 0.5, distFactor); // More opaque when close
    gl_FragColor = vec4(color, alpha);
  }
`;

// ============================================
// FIBONACCI SPHERE DISTRIBUTION
// ============================================
function fibonacciSphere(count) {
  const points = [];
  const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle
  
  for (let i = 0; i < count; i++) {
    const y = 1 - (i / (count - 1)) * 2;
    const radius = Math.sqrt(1 - y * y);
    const theta = phi * i;
    
    points.push({
      x: Math.cos(theta) * radius,
      y: y,
      z: Math.sin(theta) * radius,
      theta,
      phi: Math.acos(y)
    });
  }
  
  return points;
}

// ============================================
// STREAM PARTICLES - 1 MILLION multi-color nebula at varying depths
// ============================================
function StreamParticles({ radius = GLOBE_RADIUS, streamCount = 500, particlesPerStream = 2000 }) {
  const pointsRef = useRef();
  const materialRef = useRef();
  
  // Generate 1 million particles with color and depth variation
  const geometry = useMemo(() => {
    const totalParticles = streamCount * particlesPerStream;
    if (import.meta.env.DEV) console.log(`Creating ${totalParticles.toLocaleString()} particles...`);
    
    const positions = new Float32Array(totalParticles * 3);
    const streamAxes = new Float32Array(totalParticles * 3);
    const streamOffsets = new Float32Array(totalParticles);
    const speeds = new Float32Array(totalParticles);
    const sizes = new Float32Array(totalParticles);
    const phases = new Float32Array(totalParticles);
    const brightnesses = new Float32Array(totalParticles);
    const radiusOffsets = new Float32Array(totalParticles);
    const colorIndices = new Float32Array(totalParticles);
    const cometChances = new Float32Array(totalParticles);
    
    // ========================================
    // DRAMATIC DENSITY VARIATION
    // Some streams are THICK clusters, some are SPARSE voids
    // ========================================
    const streams = [];
    for (let s = 0; s < streamCount; s++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      
      // DENSITY - creates clusters and voids
      const densityRoll = Math.random();
      let density;
      if (densityRoll < 0.12) {
        density = 6.0;  // 12% DENSE CLUSTERS
      } else if (densityRoll < 0.35) {
        density = 2.5;  // 23% thick
      } else if (densityRoll < 0.65) {
        density = 1.0;  // 30% normal
      } else {
        density = 0.2;  // 35% SPARSE VOIDS
      }
      
      streams.push({
        ax: Math.sin(phi) * Math.cos(theta),
        ay: Math.sin(phi) * Math.sin(theta),
        az: Math.cos(phi),
        color: Math.random(),
        speed: 0.08 + Math.random() * 0.16,
        density: density
      });
    }
    
    const totalDensity = streams.reduce((sum, s) => sum + s.density, 0);
    let idx = 0;
    
    for (let s = 0; s < streamCount && idx < totalParticles; s++) {
      const stream = streams[s];
      const streamParticles = Math.floor((stream.density / totalDensity) * totalParticles);
      
      for (let p = 0; p < streamParticles && idx < totalParticles; p++) {
        // CLUSTERED distribution - knots in dense streams
        let offset;
        if (stream.density > 2.0 && Math.random() < 0.4) {
          const knotCenter = Math.random() * Math.PI * 2;
          offset = knotCenter + (Math.random() - 0.5) * 0.15;
        } else {
          offset = (p / streamParticles) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
        }
        
        const phase = Math.random() * Math.PI * 2;
        
        // CONCENTRATED at surface - fewer outer particles
        const depthRand = Math.random();
        let radiusOffset;
        if (depthRand < 0.7) {
          // 70% tight to surface
          radiusOffset = (Math.random() - 0.5) * 0.03;
        } else if (depthRand < 0.85) {
          // 15% slight inner
          radiusOffset = -0.02 - Math.random() * 0.04;
        } else if (depthRand < 0.95) {
          // 10% slight outer
          radiusOffset = 0.02 + Math.random() * 0.05;
        } else {
          // 5% far outer wisps
          radiusOffset = 0.08 + Math.random() * 0.1;
        }
        
        // COMPUTE ACTUAL POSITION on CPU
        const particleRadius = radius + radiusOffset;
        const streamAngle = offset + phase;
        
        // Start at equator point
        let x = particleRadius;
        let y = 0;
        let z = 0;
        
        // Rotate around Y axis by streamAngle
        const cosY = Math.cos(streamAngle);
        const sinY = Math.sin(streamAngle);
        const x1 = x * cosY + z * sinY;
        const z1 = -x * sinY + z * cosY;
        x = x1;
        z = z1;
        
        // Rotate around stream axis by phase * 0.5
        const axisAngle = phase * 0.5;
        const ax = stream.ax, ay = stream.ay, az = stream.az;
        const cosA = Math.cos(axisAngle);
        const sinA = Math.sin(axisAngle);
        const dot = ax * x + ay * y + az * z;
        const crossX = ay * z - az * y;
        const crossY = az * x - ax * z;
        const crossZ = ax * y - ay * x;
        
        const finalX = x * cosA + crossX * sinA + ax * dot * (1 - cosA);
        const finalY = y * cosA + crossY * sinA + ay * dot * (1 - cosA);
        const finalZ = z * cosA + crossZ * sinA + az * dot * (1 - cosA);
        
        // Normalize and scale to radius
        const len = Math.sqrt(finalX * finalX + finalY * finalY + finalZ * finalZ);
        positions[idx * 3] = (finalX / len) * particleRadius;
        positions[idx * 3 + 1] = (finalY / len) * particleRadius;
        positions[idx * 3 + 2] = (finalZ / len) * particleRadius;
        
        streamAxes[idx * 3] = stream.ax;
        streamAxes[idx * 3 + 1] = stream.ay;
        streamAxes[idx * 3 + 2] = stream.az;
        
        streamOffsets[idx] = offset;
        speeds[idx] = stream.speed * (0.4 + Math.random() * 1.2) * (stream.density < 0.5 ? 1.5 : 1.0);
        radiusOffsets[idx] = radiusOffset;
        
        // COLOR - gradient across stream with dramatic variation
        const streamProgress = p / streamParticles;
        colorIndices[idx] = stream.color + streamProgress * 0.25 + (Math.random() - 0.5) * 0.15;
        colorIndices[idx] = Math.max(0, Math.min(1, colorIndices[idx]));
        
        // SIZE & BRIGHTNESS variation - TINY particles
        const brightRoll = Math.random();
        if (brightRoll < 0.04) {
          // 4% bright stars
          sizes[idx] = 0.2 + Math.random() * 0.15;
          brightnesses[idx] = 0.9 + Math.random() * 0.1;
        } else if (brightRoll < 0.15) {
          // 11% medium-bright
          sizes[idx] = 0.12 + Math.random() * 0.1;
          brightnesses[idx] = 0.7 + Math.random() * 0.2;
        } else if (brightRoll < 0.4) {
          // 25% medium
          sizes[idx] = 0.07 + Math.random() * 0.08;
          brightnesses[idx] = 0.5 + Math.random() * 0.25;
        } else {
          // 60% small background
          sizes[idx] = 0.04 + Math.random() * 0.05;
          brightnesses[idx] = 0.3 + Math.random() * 0.3;
        }
        
        phases[idx] = phase;
        
        // Comet chance - random value, top 3% will become comets
        cometChances[idx] = Math.random();
        
        idx++;
      }
    }
    
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('aStreamAxis', new THREE.BufferAttribute(streamAxes, 3));
    geo.setAttribute('aStreamOffset', new THREE.BufferAttribute(streamOffsets, 1));
    geo.setAttribute('aSpeed', new THREE.BufferAttribute(speeds, 1));
    geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
    geo.setAttribute('aBrightness', new THREE.BufferAttribute(brightnesses, 1));
    geo.setAttribute('aRadiusOffset', new THREE.BufferAttribute(radiusOffsets, 1));
    geo.setAttribute('aColorIndex', new THREE.BufferAttribute(colorIndices, 1));
    geo.setAttribute('aCometChance', new THREE.BufferAttribute(cometChances, 1));
    
    // Larger bounding sphere for outer particles
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), radius * 2.5);
    
    if (import.meta.env.DEV) console.log('Nebula particle geometry created!');
    return geo;
  }, [streamCount, particlesPerStream, radius]);
  
  return (
    <points ref={pointsRef} geometry={geometry} renderOrder={1}>
        <shaderMaterial
          ref={materialRef}
          vertexShader={streamVertexShader}
          fragmentShader={streamFragmentShader}
        uniforms={{
          uTime: { value: 0 },
          uRadius: { value: radius }
        }}
          transparent={true}
          depthWrite={false}
          depthTest={false}
          blending={THREE.AdditiveBlending}
        />
      </points>
  );
}


// ============================================
// DARK CORE - Subtle black glow from center
// ============================================
function DarkCore({ radius = GLOBE_RADIUS * 0.85 }) {
  const materialRef = useRef();
  
  // useFrame disabled - dark core is now static
  
  return (
    <mesh>
      <sphereGeometry args={[radius, 32, 32]} />
      <shaderMaterial
        ref={materialRef}
        vertexShader={`
          varying vec3 vNormal;
          varying vec3 vViewPosition;
          
          void main() {
            vNormal = normalize(normalMatrix * normal);
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            vViewPosition = -mvPosition.xyz;
            gl_Position = projectionMatrix * mvPosition;
          }
        `}
        fragmentShader={`
          uniform float uTime;
          varying vec3 vNormal;
          varying vec3 vViewPosition;
          
          void main() {
            vec3 viewDir = normalize(vViewPosition);
            float rim = 1.0 - abs(dot(viewDir, vNormal));
            
            // Dark center, fades at edges - completely static
            float darkness = smoothstep(0.0, 0.7, 1.0 - rim);
            
            // Black color with alpha - no pulse
            gl_FragColor = vec4(0.0, 0.0, 0.0, darkness * 0.55);
          }
        `}
        uniforms={{
          uTime: { value: 0 }
        }}
        transparent={true}
        depthWrite={false}
        side={THREE.FrontSide}
      />
    </mesh>
  );
}

// ============================================
// HALO COMPONENT (Atmospheric Glow) - KEPT
// ============================================
const haloVertexShader = `
  varying vec3 vNormal;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const haloFragmentShader = `
  varying vec3 vNormal;
  uniform vec3 glowColor;
  void main() {
    float intensity = pow(0.65 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
    gl_FragColor = vec4(glowColor, intensity * 0.4);
  }
`;

function Halo() {
  return (
    <mesh scale={[1.18, 1.18, 1.18]}>
      <sphereGeometry args={[GLOBE_RADIUS, 32, 32]} />
      <shaderMaterial
        vertexShader={haloVertexShader}
        fragmentShader={haloFragmentShader}
        uniforms={{
          glowColor: { value: new THREE.Color(0x3b82f6) }
        }}
          transparent
        side={THREE.BackSide}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
}

// ============================================
// FLOATING PARTICLES (Ambient atmosphere) - KEPT
// ============================================
const floatingParticleVertexShader = `
  attribute float aRandom;
  attribute vec3 aColor;
  
  uniform float uTime;
  uniform float uSize;
  
  varying vec3 vColor;
  varying float vAlpha;
  
  void main() {
    vColor = aColor;
    
    vec3 pos = position;
    
    // Animate position with sine waves
    float t = uTime * 0.5;
    pos.x += sin(t * aRandom * 2.0 + aRandom * 6.28) * 0.15;
    pos.y += cos(t * aRandom * 1.5 + aRandom * 3.14) * 0.15;
    pos.z += sin(t * aRandom * 1.8 + aRandom * 4.71) * 0.15;
    
    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    
    // Size attenuation
    gl_PointSize = uSize * (1.0 / -mvPosition.z);
    
    // Fade particles near edges
    vAlpha = smoothstep(3.8, 1.4, length(pos));
    
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const floatingParticleFragmentShader = `
  varying vec3 vColor;
  varying float vAlpha;
  
  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    float alpha = smoothstep(0.5, 0.4, d) * vAlpha * 0.5;
    vec3 subtleColor = vColor * 1.1;
    gl_FragColor = vec4(subtleColor, alpha);
  }
`;

function FloatingParticles({ count = 150 }) {
  const pointsRef = useRef();
  const materialRef = useRef();
  const [particleSize, setParticleSize] = useState(7);
  
  useEffect(() => {
    const updateSize = () => {
      const width = window.innerWidth;
      if (width > 1920) setParticleSize(5);
      else if (width > 1440) setParticleSize(6);
      else if (width > 1024) setParticleSize(7);
      else setParticleSize(8);
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  
  const { positions, randoms, colors } = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const randoms = new Float32Array(count);
    const colors = new Float32Array(count * 3);
    
    const colorPalette = [
      [0.4, 0.8, 1.0], [0.3, 0.7, 1.0], [0.5, 0.9, 1.0],
      [1.0, 0.85, 0.0], [1.0, 0.75, 0.1], [1.0, 0.9, 0.4],
      [0.95, 0.95, 1.0], [0.85, 0.88, 1.0], [1.0, 1.0, 1.0],
    ];
    
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = 1.5 + Math.random() * 2.0;
      
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);
      
      randoms[i] = Math.random();
      
      const color = colorPalette[Math.floor(Math.random() * colorPalette.length)];
      colors[i * 3] = color[0];
      colors[i * 3 + 1] = color[1];
      colors[i * 3 + 2] = color[2];
    }
    
    return { positions, randoms, colors };
  }, [count]);
  
  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
      materialRef.current.uniforms.uSize.value = particleSize;
    }
    if (pointsRef.current) {
      pointsRef.current.rotation.y += 0.0001;
    }
  });
  
  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-aRandom" count={count} array={randoms} itemSize={1} />
        <bufferAttribute attach="attributes-aColor" count={count} array={colors} itemSize={3} />
      </bufferGeometry>
      <shaderMaterial
        ref={materialRef}
        vertexShader={floatingParticleVertexShader}
        fragmentShader={floatingParticleFragmentShader}
        uniforms={{ uTime: { value: 0 }, uSize: { value: particleSize } }}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

// ============================================
// SINGLE TOOL SPRITE WITH LOGO - KEPT
// ============================================
function ToolSprite({ tool, position, isSelected, isHovered, isHighlighted = false, isDimmed = false, isSpotlit = false, onHover, onClick }) {
  const spriteRef = useRef();
  const highlightGlowRef = useRef();
  const spotlightRingRef = useRef();
  const [texture, setTexture] = useState(() => textureCache.get(tool.name) || null);
  const [loadFailed, setLoadFailed] = useState(false);
  
  useEffect(() => {
    if (texture) return;
    const cached = textureCache.get(tool.name);
    if (cached) { setTexture(cached); return; }
    if (cached === null) { setLoadFailed(true); return; }
    
    const interval = setInterval(() => {
      const tex = textureCache.get(tool.name);
      if (tex) { setTexture(tex); clearInterval(interval); }
      else if (tex === null) { setLoadFailed(true); clearInterval(interval); }
    }, 50);
    return () => clearInterval(interval);
  }, [tool.name, texture]);
  
  const spotlightScale = isSpotlit ? 2.0 : 1.0;
  const highlightBoost = isHighlighted ? 1.2 : 1.0;
  const dimScale = isDimmed ? 0.5 : 1.0;
  const scale = (isSelected ? SELECTED_SCALE : 1.0) * highlightBoost * dimScale * spotlightScale;
  const size = POINT_SIZE * 2.5 * scale;
  const baseOpacity = isDimmed ? 0.15 : 0.95;
  const logoOffset = GLOBE_RADIUS + LOGO_OFFSET;
  
  const glareRef = useRef();
  const glareProgress = useRef(-1);
  const wasHovered = useRef(false);
  
  useFrame(({ clock }) => {
    if (isHovered && !wasHovered.current) glareProgress.current = 0;
    wasHovered.current = isHovered;
    
    if (glareRef.current && glareProgress.current >= 0 && glareProgress.current <= 1) {
      glareProgress.current += 0.04;
      const offset = (glareProgress.current - 0.5) * size * 1.5;
      glareRef.current.position.x = offset;
      const fade = Math.sin(glareProgress.current * Math.PI);
      glareRef.current.material.opacity = fade * 0.6;
      if (glareProgress.current > 1) {
        glareProgress.current = -1;
        glareRef.current.material.opacity = 0;
      }
    }
    
    if (highlightGlowRef.current && isHighlighted) {
      const pulse = 0.3 + Math.sin(clock.getElapsedTime() * 3) * 0.15;
      highlightGlowRef.current.material.opacity = pulse;
    }
    
    if (spotlightRingRef.current && isSpotlit) {
      const t = clock.getElapsedTime();
      const pulseScale = 1.8 + Math.sin(t * 2) * 0.2;
      spotlightRingRef.current.scale.set(size * pulseScale, size * pulseScale, 1);
      const pulseOpacity = 0.4 + Math.sin(t * 2.5) * 0.2;
      spotlightRingRef.current.material.opacity = pulseOpacity;
    }
  });
  
  return (
    <group position={[position.x * logoOffset, position.y * logoOffset, position.z * logoOffset]}>
      <sprite ref={glareRef} scale={[size * 0.3, size * 1.2, 1]} position={[-size, 0, 0.001]} renderOrder={11}>
        <spriteMaterial color="#ffffff" transparent opacity={0} blending={THREE.AdditiveBlending} depthTest={false} />
      </sprite>
      
      {isSpotlit && (
        <sprite ref={spotlightRingRef} scale={[size * 1.8, size * 1.8, 1]} renderOrder={7}>
          <spriteMaterial color="#14b8a6" transparent opacity={0.4} depthTest={false} blending={THREE.AdditiveBlending} />
        </sprite>
      )}
      
      {isHighlighted && (
        <sprite ref={highlightGlowRef} scale={[size * 1.6, size * 1.6, 1]} renderOrder={8}>
          <spriteMaterial color="#14b8a6" transparent opacity={0.3} depthTest={false} blending={THREE.AdditiveBlending} />
        </sprite>
      )}
      
      {isSelected && (
        <sprite scale={[size * 1.15, size * 1.15, 1]} renderOrder={9}>
          <spriteMaterial color="#10b981" transparent opacity={0.25} depthTest={false} blending={THREE.AdditiveBlending} />
        </sprite>
      )}
      
      <sprite
        ref={spriteRef}
        scale={[size, size, 1]}
        renderOrder={10}
        onPointerOver={(e) => { e.stopPropagation(); onHover(tool); }}
        onPointerOut={(e) => { e.stopPropagation(); onHover(null); }}
        onClick={(e) => { e.stopPropagation(); onClick(tool); }}
      >
        {texture && !loadFailed ? (
          <spriteMaterial map={texture} transparent opacity={baseOpacity} depthTest={false} />
        ) : (
          <spriteMaterial color={tool.bg || '#6366f1'} transparent opacity={baseOpacity} depthTest={false} />
        )}
      </sprite>
    </group>
  );
}

// ============================================
// TOOL POINTS CONTAINER - KEPT
// ============================================
function ToolPoints({ tools, selectedId, hoveredId, onHover, onClick, highlightedToolNames = [], dimmedToolNames = [], spotlightTool = null }) {
  const positions = useMemo(() => fibonacciSphere(tools.length), [tools.length]);
  const highlightedSet = useMemo(() => new Set(highlightedToolNames.map(n => n.toLowerCase())), [highlightedToolNames]);
  const dimmedSet = useMemo(() => new Set(dimmedToolNames.map(n => n.toLowerCase())), [dimmedToolNames]);
  const isSpotlightMode = spotlightTool !== null;
  const spotlightId = spotlightTool?.id;

  return (
    <group>
      {tools.map((tool, i) => {
        const isSpotlit = isSpotlightMode && tool.id === spotlightId;
        const isDimmedBySpotlight = isSpotlightMode && tool.id !== spotlightId;
        return (
          <ToolSprite
            key={tool.id}
            tool={tool}
            position={positions[i]}
            isSelected={tool.id === selectedId}
            isHovered={tool.id === hoveredId}
            isHighlighted={highlightedSet.has(tool.name.toLowerCase()) || isSpotlit}
            isDimmed={(dimmedSet.size > 0 && !highlightedSet.has(tool.name.toLowerCase())) || isDimmedBySpotlight}
            isSpotlit={isSpotlit}
            onHover={onHover}
            onClick={onClick}
          />
        );
      })}
    </group>
  );
}

// ============================================
// CONNECTION LINES - KEPT
// ============================================
const _tv1 = new THREE.Vector3();
const _tv2 = new THREE.Vector3();
const _tStart = new THREE.Vector3();
const _tEnd = new THREE.Vector3();

function TravelingPulse({ p1, p2, radius, color, duration, delay }) {
  const groupRef = useRef();
  
  const trailSegments = useMemo(() => {
    const segments = [];
    const numSegments = 6;
    for (let i = 0; i < numSegments; i++) {
      segments.push({
        geometry: new THREE.BufferGeometry(),
        opacity: Math.pow(1 - (i / numSegments), 1.5)
      });
      const positions = new Float32Array(6);
      segments[i].geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    }
    return segments;
  }, []);
  
  const v1Cached = useMemo(() => new THREE.Vector3(p1.x, p1.y, p1.z).normalize(), [p1]);
  const v2Cached = useMemo(() => new THREE.Vector3(p2.x, p2.y, p2.z).normalize(), [p2]);
  
  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    
    const time = clock.getElapsedTime();
    const pauseTime = 3 + (delay % 5);
    const cycleTime = duration + pauseTime;
    const t = ((time + delay) % cycleTime) / duration;
    
    const trailLength = 0.5;
    const headPos = t;
    const numSegments = trailSegments.length;
    
    trailSegments.forEach((seg, i) => {
      const segStart = headPos - (trailLength * i / numSegments);
      const segEnd = headPos - (trailLength * (i + 1) / numSegments);
      const startT = Math.max(0, Math.min(1, segStart));
      const endT = Math.max(0, Math.min(1, segEnd));
      
      _tStart.copy(v1Cached).lerp(_tv1.copy(v2Cached), startT).normalize().multiplyScalar(radius);
      _tEnd.copy(v1Cached).lerp(_tv2.copy(v2Cached), endT).normalize().multiplyScalar(radius);
      
      const positions = seg.geometry.attributes.position.array;
      positions[0] = _tStart.x; positions[1] = _tStart.y; positions[2] = _tStart.z;
      positions[3] = _tEnd.x; positions[4] = _tEnd.y; positions[5] = _tEnd.z;
      seg.geometry.attributes.position.needsUpdate = true;
      
      const child = groupRef.current.children[i];
      if (child && child.material) {
        const isVisible = t <= 1.1 && segStart > 0;
        const baseOpacity = seg.opacity * 0.7;
        const fadeIn = Math.min(1, headPos * 4);
        child.material.opacity = isVisible ? baseOpacity * fadeIn : 0;
      }
    });
  });
  
  return (
    <group ref={groupRef} renderOrder={-1}>
      {trailSegments.map((seg, i) => (
        <line key={i} geometry={seg.geometry} renderOrder={-1}>
          <lineBasicMaterial color={color} transparent opacity={0} blending={THREE.AdditiveBlending} depthTest={false} />
        </line>
      ))}
    </group>
  );
}

function createSphereArc(p1, p2, radius, segments = 32) {
  const points = [];
  const v1 = new THREE.Vector3(p1.x, p1.y, p1.z).normalize();
  const v2 = new THREE.Vector3(p2.x, p2.y, p2.z).normalize();
  
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const point = new THREE.Vector3().copy(v1).lerp(v2, t).normalize().multiplyScalar(radius);
    points.push(point.x, point.y, point.z);
  }
  
  return new Float32Array(points);
}

function ConnectionLines({ tools }) {
  const positions = useMemo(() => fibonacciSphere(tools.length), [tools.length]);
  
  const connections = useMemo(() => {
    const candidates = [];
    const maxDistance = 0.65;
    const arcRadius = GLOBE_RADIUS + LOGO_OFFSET;
    const arcSegments = 32;
    const MAX_CONNECTIONS = 40;
    
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const p1 = positions[i];
        const p2 = positions[j];
        const dx = p1.x - p2.x, dy = p1.y - p2.y, dz = p1.z - p2.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        
        if (dist < maxDistance) {
          candidates.push({ i, j, dist });
        }
      }
    }
    
    candidates.sort((a, b) => a.dist - b.dist);
    const selected = candidates.slice(0, MAX_CONNECTIONS);
    
    return selected.map(({ i, j }) => {
      const p1 = positions[i];
      const p2 = positions[j];
      const isGold = (i + j) % 2 === 0;
      const arcPoints = createSphereArc(p1, p2, arcRadius, arcSegments);
      return {
        p1, p2, arcRadius,
        points: arcPoints,
        segmentCount: arcSegments + 1,
        color: isGold ? '#FFD700' : '#C0C0C0',
        duration: 4 + Math.random() * 6,
        delay: Math.random() * 20
      };
    });
  }, [positions]);
  
  return (
    <group renderOrder={-1}>
      {connections.map((conn, i) => (
        <line key={`static-${i}`} renderOrder={-2}>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" count={conn.segmentCount} array={conn.points} itemSize={3} />
          </bufferGeometry>
          <lineBasicMaterial color={conn.color} transparent opacity={0.08} blending={THREE.AdditiveBlending} depthTest={false} />
        </line>
      ))}
      {/* Traveling pulses */}
      {connections.map((conn, i) => (
        <TravelingPulse key={`pulse-${i}`} p1={conn.p1} p2={conn.p2} radius={conn.arcRadius} color={conn.color} duration={conn.duration} delay={conn.delay} />
      ))}
    </group>
  );
}

// ============================================
// GLOBE CONTROLS - KEPT
// ============================================
function GlobeControls({ globeRef, selectedTool, positions, tools, onZoomChange }) {
  const isDragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const velocity = useRef({ x: IDLE_ROTATION_SPEED, y: 0 });
  const lastPinchDist = useRef(0);
  const targetQuaternion = useRef(null);
  const isAnimatingToTarget = useRef(false);
  
  useEffect(() => {
    if (!selectedTool || !globeRef.current) return;
    
    const toolIndex = tools.findIndex(t => t.id === selectedTool.id);
    if (toolIndex === -1) return;
    
    const pos = positions[toolIndex];
    if (!pos) return;
    
    const pointVector = new THREE.Vector3(pos.x, pos.y, pos.z).normalize();
    const targetVector = new THREE.Vector3(0, 0, 1);
    const dot = pointVector.dot(targetVector);
    const rotationQuat = new THREE.Quaternion();
    
    if (dot < -0.999999) rotationQuat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
    else if (dot > 0.999999) rotationQuat.identity();
    else rotationQuat.setFromUnitVectors(pointVector, targetVector);
    
    targetQuaternion.current = rotationQuat;
    isAnimatingToTarget.current = true;
    velocity.current = { x: 0, y: 0 };
  }, [selectedTool, tools, positions, globeRef]);
  
  useFrame(() => {
    if (!globeRef.current) return;
    const globe = globeRef.current;
    
    if (isAnimatingToTarget.current && targetQuaternion.current) {
      globe.quaternion.slerp(targetQuaternion.current, 0.04);
      const dot = Math.abs(globe.quaternion.dot(targetQuaternion.current));
      if (dot > 0.9998) {
        isAnimatingToTarget.current = false;
        targetQuaternion.current = null;
      }
    } else if (!isDragging.current) {
      // Momentum after drag
      if (Math.abs(velocity.current.x) > 0.00001 || Math.abs(velocity.current.y) > 0.00001) {
        const rotX = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), velocity.current.y);
        const rotY = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), velocity.current.x);
        globe.quaternion.premultiply(rotY);
        globe.quaternion.premultiply(rotX);
        velocity.current.x *= MOMENTUM_DECAY;
        velocity.current.y *= MOMENTUM_DECAY;
      }
      // Idle rotation
      if (!selectedTool && Math.abs(velocity.current.x) < 0.0001) {
        velocity.current.x = IDLE_ROTATION_SPEED;
      }
    }
  });
  
  useEffect(() => {
    const handleMouseDown = (e) => {
      isDragging.current = true;
      lastMouse.current = { x: e.clientX, y: e.clientY };
      isAnimatingToTarget.current = false;
      targetQuaternion.current = null;
    };
    
    const handleMouseMove = (e) => {
      if (!isDragging.current || !globeRef.current) return;
      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      
      const rotX = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), dy * DRAG_SENSITIVITY);
      const rotY = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), dx * DRAG_SENSITIVITY);
      
      globeRef.current.quaternion.premultiply(rotY);
      globeRef.current.quaternion.premultiply(rotX);
      
      velocity.current.x = dx * DRAG_SENSITIVITY * 0.5;
      velocity.current.y = dy * DRAG_SENSITIVITY * 0.5;
      lastMouse.current = { x: e.clientX, y: e.clientY };
    };
    
    const handleMouseUp = () => { isDragging.current = false; };

    // Touch handlers for mobile
    const getTouchDistance = (t1, t2) => {
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const isOverGlobe = (target) => {
      return target.closest('canvas') || target.tagName === 'CANVAS';
    };

    const handleTouchStart = (e) => {
      if (!isOverGlobe(e.target)) return;
      e.preventDefault();
      if (e.touches.length === 1) {
        isDragging.current = true;
        lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        isAnimatingToTarget.current = false;
        targetQuaternion.current = null;
        lastPinchDist.current = 0;
      } else if (e.touches.length === 2) {
        isDragging.current = false;
        lastPinchDist.current = getTouchDistance(e.touches[0], e.touches[1]);
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const dist = getTouchDistance(e.touches[0], e.touches[1]);
        if (lastPinchDist.current > 0) {
          const scale = dist / lastPinchDist.current;
          const delta = (scale - 1) * 0.4;
          onZoomChange?.(delta);
        }
        lastPinchDist.current = dist;
        return;
      }

      if (!isDragging.current || !globeRef.current || e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0];
      const dx = touch.clientX - lastMouse.current.x;
      const dy = touch.clientY - lastMouse.current.y;

      const rotX = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), dy * DRAG_SENSITIVITY);
      const rotY = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), dx * DRAG_SENSITIVITY);

      globeRef.current.quaternion.premultiply(rotY);
      globeRef.current.quaternion.premultiply(rotX);

      velocity.current.x = dx * DRAG_SENSITIVITY * 0.5;
      velocity.current.y = dy * DRAG_SENSITIVITY * 0.5;
      lastMouse.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = () => {
      isDragging.current = false;
      lastPinchDist.current = 0;
    };

    const handleGestureStart = (e) => e.preventDefault();
    
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mouseleave', handleMouseUp);
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);
    window.addEventListener('gesturestart', handleGestureStart);
    
    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mouseleave', handleMouseUp);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
      window.removeEventListener('gesturestart', handleGestureStart);
    };
  }, [globeRef]);
  
  return null;
}

// ============================================
// STARFIELD - Dramatic twinkling stars
// ============================================
function Starfield({ count = 1500 }) {
  const pointsRef = useRef();
  const materialRef = useRef();
  
  const { positions, sizes, twinklePhases, colors } = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const twinklePhases = new Float32Array(count);
    const colors = new Float32Array(count * 3);
    
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 3 + Math.random() * 10;
      
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
      
      // Varied sizes - some bright stars
      const sizeRoll = Math.random();
      if (sizeRoll > 0.98) {
        sizes[i] = 4 + Math.random() * 3; // Big bright stars
      } else if (sizeRoll > 0.9) {
        sizes[i] = 2 + Math.random() * 2;
      } else {
        sizes[i] = 0.5 + Math.random() * 1.5;
      }
      
      twinklePhases[i] = Math.random() * Math.PI * 2;
      
      // Star colors - white, blue, gold
      const colorRoll = Math.random();
      if (colorRoll < 0.6) {
        colors[i * 3] = 0.9; colors[i * 3 + 1] = 0.95; colors[i * 3 + 2] = 1.0; // White-blue
      } else if (colorRoll < 0.85) {
        colors[i * 3] = 0.4; colors[i * 3 + 1] = 0.6; colors[i * 3 + 2] = 1.0; // Blue
      } else {
        colors[i * 3] = 1.0; colors[i * 3 + 1] = 0.85; colors[i * 3 + 2] = 0.5; // Gold
      }
    }
    
    return { positions, sizes, twinklePhases, colors };
  }, [count]);
  
  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
    }
  });
  
  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-aSize" count={count} array={sizes} itemSize={1} />
        <bufferAttribute attach="attributes-aPhase" count={count} array={twinklePhases} itemSize={1} />
        <bufferAttribute attach="attributes-aColor" count={count} array={colors} itemSize={3} />
      </bufferGeometry>
      <shaderMaterial
        ref={materialRef}
        uniforms={{ uTime: { value: 0 } }}
        vertexShader={`
          attribute float aSize;
          attribute float aPhase;
          attribute vec3 aColor;
          uniform float uTime;
          varying float vTwinkle;
          varying vec3 vColor;
          
          void main() {
            vColor = aColor;
            vTwinkle = 0.3 + 0.7 * sin(uTime * 1.5 + aPhase);
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = aSize * (30.0 / -mvPosition.z);
            gl_Position = projectionMatrix * mvPosition;
          }
        `}
        fragmentShader={`
          varying float vTwinkle;
          varying vec3 vColor;
          
          void main() {
            vec2 center = gl_PointCoord - vec2(0.5);
            float dist = length(center) * 2.0;
            if (dist > 0.7) discard;
            
            // Very sharp bright core
            float core = smoothstep(0.4, 0.0, dist);
            float alpha = core * vTwinkle;
            
            gl_FragColor = vec4(vColor * 1.3, alpha);
          }
        `}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

// ============================================
// NEBULA - Dramatic colorful space clouds
// ============================================
function Nebula() {
  const materialRef = useRef();
  
  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
    }
  });
  
  return (
    <mesh scale={[12, 12, 12]}>
      <sphereGeometry args={[1, 32, 32]} />
      <shaderMaterial
        ref={materialRef}
        uniforms={{ uTime: { value: 0 } }}
        vertexShader={`
          varying vec3 vNormal;
          varying vec3 vPosition;
          varying vec2 vUv;
          
          void main() {
            vNormal = normalize(normalMatrix * normal);
            vPosition = position;
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
        fragmentShader={`
          uniform float uTime;
          varying vec3 vNormal;
          varying vec3 vPosition;
          varying vec2 vUv;
          
          // Simple noise
          float noise(vec2 p) {
            return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
          }
          
          float smoothNoise(vec2 p) {
            vec2 i = floor(p);
            vec2 f = fract(p);
            f = f * f * (3.0 - 2.0 * f);
            
            float a = noise(i);
            float b = noise(i + vec2(1.0, 0.0));
            float c = noise(i + vec2(0.0, 1.0));
            float d = noise(i + vec2(1.0, 1.0));
            
            return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
          }
          
          float fbm(vec2 p) {
            float value = 0.0;
            float amplitude = 0.5;
            for (int i = 0; i < 4; i++) {
              value += amplitude * smoothNoise(p);
              p *= 2.0;
              amplitude *= 0.5;
            }
            return value;
          }
          
          void main() {
            vec2 uv = vUv * 4.0;
            float t = uTime * 0.05;
            
            // Animated nebula clouds
            float n1 = fbm(uv + t);
            float n2 = fbm(uv * 1.5 - t * 0.5);
            float n3 = fbm(uv * 0.8 + vec2(t * 0.3, -t * 0.2));
            
            // Color layers - more subtle blues
            vec3 color1 = vec3(0.05, 0.1, 0.25) * n1; // Subtle deep blue
            vec3 color2 = vec3(0.3, 0.08, 0.35) * n2; // Purple
            vec3 color3 = vec3(0.05, 0.2, 0.2) * n3; // Subtle teal
            vec3 color4 = vec3(0.5, 0.25, 0.08) * n1 * n2; // Gold accents
            
            vec3 color = color1 + color2 + color3 + color4 * 0.5;
            
            // Rim fade
            float rim = 1.0 - abs(dot(vNormal, vec3(0.0, 0.0, 1.0)));
            rim = pow(rim, 1.8);
            
            float alpha = rim * (n1 + n2) * 0.2;
            
            gl_FragColor = vec4(color * 1.5, alpha);
          }
        `}
        transparent
        side={THREE.BackSide}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

// ============================================
// MAIN GLOBE SCENE - EDGE FLOW PARTICLES
// ============================================
function GlobeScene({ tools, selectedTool, hoveredTool, onHover, onClick, onZoomChange, highlightedToolNames = [], dimmedToolNames = [], spotlightTool = null, quality }) {
  const globeRef = useRef();
  const positions = useMemo(() => fibonacciSphere(tools.length), [tools.length]);
  
  return (
    <>
      {/* Background effects */}
      <Starfield count={800} />
      <Nebula />
      
      {/* Lighting - softer for ethereal */}
      <ambientLight intensity={0.25} />
      <pointLight position={[10, 10, 10]} intensity={0.4} />
      <pointLight position={[-10, -10, -10]} intensity={0.15} color="#6366f1" />
      <pointLight position={[0, 10, -10]} intensity={0.25} color="#3b82f6" />
      <pointLight position={[0, -10, 10]} intensity={0.15} color="#8b5cf6" />
      
      {/* Outer floating particles - disabled for testing */}
      {/* <FloatingParticles count={100} /> */}
      
      {/* Atmospheric halo - disabled for testing */}
      {/* <Halo /> */}
      
      {/* Globe group */}
      <group ref={globeRef}>
        {/* Dark void in center */}
        <DarkCore radius={GLOBE_RADIUS * 0.82} />
        
        {/* Stream particles */}
        <StreamParticles 
          radius={GLOBE_RADIUS}
          streamCount={80}
          particlesPerStream={400}
        />
        
        {/* Connection lines between tools */}
        <ConnectionLines tools={tools} />
        
        {/* Tool logos */}
        <ToolPoints
          tools={tools}
          selectedId={selectedTool?.id}
          hoveredId={hoveredTool?.id}
          onHover={onHover}
          onClick={onClick}
          highlightedToolNames={highlightedToolNames}
          dimmedToolNames={dimmedToolNames}
          spotlightTool={spotlightTool}
        />
      </group>
      
      {/* Controls */}
      <GlobeControls
        globeRef={globeRef}
        selectedTool={selectedTool}
        positions={positions}
        tools={tools}
        onZoomChange={onZoomChange}
      />
    </>
  );
}

// ============================================
// QUALITY MONITOR - DISABLED (was causing quality changes that flicker)
// ============================================
function QualityMonitor({ onQualityChange }) {
  // Disabled to prevent flickering from quality changes
  return null;
}

// ============================================
// CAMERA ZOOM CONTROLLER
// ============================================
function CameraZoomController({ zoomLevel }) {
  const { camera } = useThree();
  const animRef = useRef({ startZ: 2.5, targetZ: 2.5, startTime: 0, duration: 0.8, animating: false });
  
  useEffect(() => {
    const baseDistance = 2.5;
    const newTarget = baseDistance / zoomLevel;
    const anim = animRef.current;
    
    if (Math.abs(anim.targetZ - newTarget) > 0.001) {
      anim.startZ = camera.position.z;
      anim.targetZ = newTarget;
      anim.startTime = performance.now();
      anim.animating = true;
    }
  }, [zoomLevel, camera]);
  
  useFrame(() => {
    const anim = animRef.current;
    if (!anim.animating) return;
    
    const elapsed = (performance.now() - anim.startTime) / 1000;
    const t = Math.min(elapsed / anim.duration, 1);
    const eased = 1 - Math.pow(1 - t, 3);
    
    camera.position.z = anim.startZ + (anim.targetZ - anim.startZ) * eased;
    camera.updateProjectionMatrix();
    
    if (t >= 1) anim.animating = false;
  });
  
  return null;
}

// ============================================
// EXPORTED GLOBE COMPONENT
// ============================================
export default function Globe({
  tools,
  selectedTool,
  hoveredTool,
  onHover,
  onClick,
  zoomLevel = 1,
  onZoomChange,
  className = '',
  onReady,
  highlightedToolNames = [],
  dimmedToolNames = [],
  spotlightTool = null
}) {
  const [quality, setQuality] = useState('high');
  const [isReady, setIsReady] = useState(false);
  const texturesLoadedRef = useRef(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    if (!onReady) return;
    
    // If textures are already loaded, call onReady immediately
    if (texturesLoadedRef.current) {
      setTimeout(() => onReady(), 50);
      return;
    }
    
    const checkTextures = () => {
      const loadedCount = Array.from(textureCache.values()).filter(t => t !== null && t !== undefined).length;
      const totalTools = tools.length;
      const loadedRatio = loadedCount / totalTools;
      
      if (loadedRatio >= 0.8 || loadedCount > 50) {
        texturesLoadedRef.current = true;
        setTimeout(() => onReady(), 300);
        return;
      }
    };
    
    checkTextures();
    const interval = setInterval(checkTextures, 200);
    const fallback = setTimeout(() => {
      if (!texturesLoadedRef.current) {
        texturesLoadedRef.current = true;
        onReady();
      }
      clearInterval(interval);
    }, 2000);
    
    return () => { clearInterval(interval); clearTimeout(fallback); };
  }, [onReady, tools.length]);
  
  return (
    <div className={`relative w-full h-full ${className}`} style={{ touchAction: 'none' }}>
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-64 h-64 rounded-full bg-white/5 animate-pulse" />
        </div>
      )}
      
      <Canvas
        camera={{ position: [0, 0, 2.5], fov: 60 }}
        dpr={[1, 1.5]}
        gl={{ antialias: false, alpha: true, powerPreference: 'high-performance' }}
        style={{ opacity: isReady ? 1 : 0, transition: 'opacity 0.3s ease-in-out' }}
      >
        <GlobeScene
          tools={tools}
          selectedTool={selectedTool}
          hoveredTool={hoveredTool}
          onHover={onHover}
          onClick={onClick}
          onZoomChange={onZoomChange}
          highlightedToolNames={highlightedToolNames}
          dimmedToolNames={dimmedToolNames}
          spotlightTool={spotlightTool}
          quality={quality}
        />
        <CameraZoomController zoomLevel={zoomLevel} />
        <QualityMonitor onQualityChange={setQuality} />
      </Canvas>
      
      {/* Vignette overlay - responds to zoom level */}
      <div 
        className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{
          background: `
            radial-gradient(ellipse at center, transparent ${5 + zoomLevel * 15}%, rgba(10,5,30,${Math.max(0.1, 0.5 - zoomLevel * 0.15)}) ${25 + zoomLevel * 10}%, rgba(5,0,15,${Math.max(0.2, 0.85 - zoomLevel * 0.25)}) ${45 + zoomLevel * 10}%, rgba(0,0,5,${Math.max(0.3, 0.98 - zoomLevel * 0.3)}) 100%)
          `
        }}
      />
    </div>
  );
}
